import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";

// Global CSS for carousels
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Layout components
import Navbar from "./components/Users/Navbar";
import Footer from "./components/Users/Footer";
import PopupCard from "./components/Users/PopupCard";
import MaintenancePage from "./components/Users/MaintenancePage";

// Pages
import HomePage from "./components/Users/HomePage";
import SignUp from "./components/Users/SignUp";
import UserDetails from "./components/Users/UserDetails";
import UserProfileSection from "./components/Users/UserProfileSection";
import MashoStudioPage from "./components/Users/MashoStudioPage";
import ShoppingPage from "./components/Users/ShoppingPage";
import AboutUs from "./components/Users/AboutUs";
import TrackOrder from "./components/Users/TrackOrder";
import CartPage from "./components/Users/CartPage";
import ProductDetail from "./components/Users/ProductDetail";
import BillingPage from "./components/Users/BillingPage";
import NewArrivalPage from "./components/Users/NewArrivalPage";
import TestimonialPage from "./components/Users/TestimonialPage";
import ReturnPolicy from "./components/Users/ReturnPolicy";
import ShippingPolicy from "./components/Users/ShippingPolicy";
import PrivacyPolicy from "./components/Users/PrivacyPolicy";
import TermsAndConditions from "./components/Users/TermsAndConditions";
import OAuthCallback from "./components/Users/OAuthCallback";
import OrderSuccess from "./components/Users/OrderSuccess";
import RefundPolicy from "./components/Users/RefundPolicy";
import LogoutButton from "./components/Users/LogoutButton";
import Mashologo from "../src/assets/images/Logo for website/Desktop.png";
import PaymentCallback from "./components/Users/PaymentCallback";
import OrderFailure from "./components/Users/OrderFailure";
import ScrollToTop from "./components/Users/ScrollToTop";
import PerfumesList from "./components/Users/PerfumesList";
import AttarList from "./components/Users/AttarList";

// Auth0 configuration
const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: window.location.origin + "/oauth/callback",
  audience: "https://masho-api/",
  scope: "openid profile email"
};

// Create Authentication Context
const AuthContext = createContext();

// Auth Provider Component that wraps Auth0 provider
const Auth0ProviderWithHistory = ({ children }) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope
      }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

// Custom Auth Provider that uses Auth0's hooks
const AuthProvider = ({ children }) => {
  const { 
    isAuthenticated, 
    isLoading, 
    user, 
    loginWithRedirect, 
    logout: auth0Logout,
    getAccessTokenSilently 
  } = useAuth0();
  
  // Custom login function
  const login = () => {
    return loginWithRedirect();
  };

  // Custom logout function
  const logout = () => {
    return auth0Logout({ 
      returnTo: window.location.origin 
    });
  };

  // Get access token function
  const getToken = async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      console.error("Failed to get access token", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isLoading, 
      user,
      login,
      logout,

    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use authentication context
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth popup component
const AuthPopup = ({ onClose }) => {
  const navigate = useNavigate();
  
  const handleLogin = () => {
    navigate("/profile");
    onClose();
  };
  
  return (
    <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-4 max-w-xs z-50 border border-gray-200">
      <div className="flex justify-between items-start">
        <h3 className="font-semibold text-gray-800">Sign in to your account</h3>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>
      <p className="text-sm text-gray-600 my-2">
        Please sign in to access all features and personalized content.
      </p>
      <div className="flex justify-end mt-3">
        <button 
          onClick={handleLogin}
          className="px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition-colors"
        >
          Sign In
        </button>
      </div>
    </div>
  );
};

// Layout wrappers
const MainLayout = ({ children, showAuthPopup, setShowAuthPopup }) => {
  const { isAuthenticated } = useAuth();
  
  return (
    <>
      <Navbar />
      <PopupCard />
      <main>{children}</main>
      <Footer />
      {!isAuthenticated && (
        <AuthPopup onClose={() => setShowAuthPopup(true)} />
      )}
    </>
  );
};

const AuthLayout = ({ children }) => (
  <>
    <main>{children}</main>
  </>
);

// Protected route wrapper
const ProtectedRoute = ({ element, setShowAuthPopup }) => {
  const { isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (!isAuthenticated) {
      setShowAuthPopup(true);
    }
  }, [isAuthenticated, setShowAuthPopup]);
  
  return element;
};

function App() {
  const [isMaintenance, setIsMaintenance] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState({
    maintenance_message: "We are currently performing scheduled maintenance. We should be back online shortly.",
    estimated_time: "1 hour"
  });
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();

  // Hide auth popup when navigating to profile page
  useEffect(() => {
    if (location.pathname === "/profile") {
      setShowAuthPopup(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get('https://api8714920733.mashoperfumes.com/maintainance-mode/');
        const data = response.data;
        
        if (data.is_maintenance) {
          setIsMaintenance(true);
          setMaintenanceData({
            maintenance_message: data.maintenance_message,
            estimated_time: data.estimated_time
          });
        }
      } catch (error) {
        console.error("Error checking maintenance mode:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkMaintenanceMode();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          {/* Blinking Masho Logo */}
          <div className="mb-6">
            <img 
              src={Mashologo} 
              alt="Masho Logo" 
              className="h-1/4 w-1/4 mx-auto"
              style={{
                animation: 'blink 1.5s ease-in-out infinite'
              }}
            />
          </div>
          
          {/* Golden Yellow Loading Line */}
          <div className="w-64 h-1 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full"
              style={{
                animation: 'loadingLine 2s ease-in-out infinite'
              }}
            ></div>
          </div>
          
          <p className="text-gray-700 font-medium">Loading application...</p>
        </div>
        
        <style jsx>{`
          // @keyframes blink {
          //   0%, 100% { opacity: 1; }
          //   50% { opacity: 0.3; }
          // }
          
          @keyframes loadingLine {
            0% { width: 0%; transform: translateX(-100%); }
            50% { width: 100%; transform: translateX(0%); }
            100% { width: 0%; transform: translateX(100%); }
          }
        `}</style>
      </div>
    );
  }

  if (isMaintenance) {
    return (
      <MaintenancePage 
        message={maintenanceData.maintenance_message} 
        estimatedTime={maintenanceData.estimated_time} 
      />
    );
  }

  // Common layout props
  const layoutProps = {
    showAuthPopup,
    setShowAuthPopup
  };

  return (
    <AuthProvider>
      <ScrollToTop/>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<MainLayout {...layoutProps}><HomePage /></MainLayout>} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/masho-studio" element={<MainLayout {...layoutProps}><MashoStudioPage /></MainLayout>} />
        <Route path="/shop" element={<MainLayout {...layoutProps}><ShoppingPage /></MainLayout>} />
        <Route path="/perfumes" element={<MainLayout {...layoutProps}><PerfumesList /></MainLayout>} />
        <Route path="/attars" element={<MainLayout {...layoutProps}><AttarList /></MainLayout>} />
        <Route path="/about" element={<MainLayout {...layoutProps}><AboutUs /></MainLayout>} />
        <Route path="/tracking" element={<MainLayout {...layoutProps}><TrackOrder /></MainLayout>} />
        <Route path="/product/:id" element={<MainLayout {...layoutProps}><ProductDetail /></MainLayout>} />
        <Route path="/new-arrivals" element={<MainLayout {...layoutProps}><NewArrivalPage /></MainLayout>} />
        <Route path="/testimonials" element={<MainLayout {...layoutProps}><TestimonialPage /></MainLayout>} />
        <Route path="/return-policy" element={<MainLayout {...layoutProps}><ReturnPolicy /></MainLayout>} />
        <Route path="/refund-policy" element={<MainLayout {...layoutProps}><RefundPolicy /></MainLayout>} />
        <Route path="/shipping-policy" element={<MainLayout {...layoutProps}><ShippingPolicy /></MainLayout>} />
        <Route path="/privacy-policy" element={<MainLayout {...layoutProps}><PrivacyPolicy /></MainLayout>} />
        <Route path="/terms-and-conditions" element={<MainLayout {...layoutProps}><TermsAndConditions /></MainLayout>} />
        <Route path="/logoutt" element={<MainLayout {...layoutProps}><LogoutButton /></MainLayout>} />
        <Route path="/signup" element={<AuthLayout><SignUp /></AuthLayout>} />
        <Route path="/payment-callback" element={<PaymentCallback />} />
        
        {/* Protected routes - show auth popup if not authenticated but still allow access */}
        <Route 
          path="/user-details" 
          element={
            <MainLayout {...layoutProps}>
              <ProtectedRoute 
                element={<UserDetails />} 
                setShowAuthPopup={setShowAuthPopup}
              />
            </MainLayout>
          } 
        />
        <Route 
          path="/profile" 
          element={
            <MainLayout {...layoutProps}>
              <UserProfileSection />
            </MainLayout>
          } 
        />
        <Route 
          path="/cart" 
          element={
            <MainLayout {...layoutProps}>
              <ProtectedRoute 
                element={<CartPage />} 
                setShowAuthPopup={setShowAuthPopup}
              />
            </MainLayout>
          } 
        />
        <Route 
          path="/billing" 
          element={
            <MainLayout {...layoutProps}>
              <ProtectedRoute 
                element={<BillingPage />} 
                setShowAuthPopup={setShowAuthPopup}
              />
            </MainLayout>
          } 
        />
        <Route 
          path="/success" 
          element={
            <MainLayout {...layoutProps}>
              <ProtectedRoute 
                element={<OrderSuccess />}
                setShowAuthPopup={setShowAuthPopup}
              />
            </MainLayout>
          } 
        />
        <Route 
          path="/order-failure" 
          element={
            <MainLayout {...layoutProps}>
              <ProtectedRoute 
                element={<OrderFailure />}
                setShowAuthPopup={setShowAuthPopup}
              />
            </MainLayout>
          } 
        />

        {/* Fallback route */}
        <Route path="*" element={
          <MainLayout {...layoutProps}>
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
              <h1 className="text-4xl font-bold text-gray-800 mb-4">404</h1>
              <p className="text-xl text-gray-600 mb-6">Page Not Found</p>
              <a 
                href="/" 
                className="px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
              >
                Return to Home
              </a>
            </div>
          </MainLayout>
        } />
      </Routes>
    </AuthProvider>
  );
}

export default App;
